<template>
  <section class="news-article-body" @click="handleLinkClick">
    <template v-for="(block, index) in displayBlocks" :key="`${block.type}-${index}`">
      <h2
        v-if="block.type === 'heading' && block.level === 2"
        :class="{ 'is-framed-heading': block.framed }"
        :style="headingStyle(block)"
      >{{ block.text }}</h2>
      <h3
        v-else-if="block.type === 'heading'"
        :class="{ 'is-framed-heading': block.framed }"
        :style="headingStyle(block)"
      >{{ block.text }}</h3>
      <p v-else-if="block.type === 'paragraph'" v-html="linkedText(block.text)"></p>
      <div
        v-else-if="block.type === 'html' && block.tag === 'table'"
        class="news-article-table-block"
        v-html="linkedHtml(block.html)"
      ></div>
      <p v-else-if="block.type === 'html' && block.tag === 'p'" v-html="linkedHtml(block.html)"></p>
      <blockquote v-else-if="block.type === 'quote'" v-html="linkedHtml(block.html || block.text)"></blockquote>
      <div v-else-if="block.type === 'reddit'" class="news-reddit-embed">
        <blockquote class="reddit-embed-bq" data-embed-height="740">
          <a :href="block.url" target="_blank" rel="noopener noreferrer">View this post on Reddit</a>
        </blockquote>
      </div>
      <aside v-else-if="block.type === 'bluepost'" class="news-bluepost">
        <header>
          <span><i class="fa-brands fa-battle-net"></i> {{ block.title || 'Blizzard' }}</span>
          <a v-if="block.url" :href="block.url" target="_blank" rel="noreferrer">
            Source
            <i class="fa-solid fa-arrow-up-right-from-square"></i>
          </a>
        </header>
        <div v-if="block.blocks?.length" class="news-bluepost-content">
          <NewsArticleContent :blocks="block.blocks" @open-source-article="$emit('open-source-article', $event)" />
        </div>
        <div v-else class="news-bluepost-content" v-html="linkedHtml(block.html)"></div>
      </aside>
      <section v-else-if="block.type === 'group'" class="news-article-group">
        <header v-if="block.title">
          <h3 :style="headingStyle(block)">{{ block.title }}</h3>
        </header>
        <div class="news-article-group-columns">
          <div
            v-for="column in block.columns"
            :key="column.title"
            class="news-article-group-column"
            :class="column.kind ? `is-${column.kind}` : ''"
          >
            <p v-if="column.title" class="news-article-group-column-title">{{ column.title }}</p>
            <ul>
              <li v-for="(item, itemIndex) in column.items" :key="itemIndex" v-html="linkedHtml(item)"></li>
            </ul>
          </div>
        </div>
      </section>
      <section v-else-if="block.type === 'cardlist'" class="news-article-cardlist">
        <div v-for="section in block.sections" :key="section.title" class="news-article-cardlist-section">
          <h3 v-if="section.title">{{ section.title }}</h3>
          <div class="news-article-cardlist-grid">
            <a
              v-for="card in section.cards"
              :key="card.url"
              class="news-article-card"
              :href="card.url"
            >
              <span v-if="card.imageUrl" class="news-article-card-media">
                <img :src="card.imageUrl" :alt="card.title" loading="lazy" @error="handleImageError">
              </span>
              <span class="news-article-card-body">
                <span>
                  <span class="news-article-card-title">{{ card.title }}</span>
                  <span v-if="card.subtitle" class="news-article-card-subtitle">{{ card.subtitle }}</span>
                </span>
                <span v-if="card.cta" class="news-article-card-cta">{{ card.cta }} <i class="fa-solid fa-arrow-right"></i></span>
              </span>
            </a>
          </div>
        </div>
      </section>
      <hr v-else-if="block.type === 'separator'" class="news-article-separator">
      <div v-else-if="block.type === 'image' && block.placeholder" class="news-article-media-placeholder">
        <i class="fa-solid fa-image"></i>
        <span>{{ block.alt || 'Image unavailable' }}</span>
      </div>
      <figure v-else-if="block.type === 'image' && block.caption" class="news-article-figure">
        <img :src="block.src" :alt="block.alt || ''" loading="lazy" @error="handleImageError">
        <figcaption v-html="linkedHtml(block.caption)"></figcaption>
      </figure>
      <img
        v-else-if="block.type === 'image'"
        :src="block.src"
        :alt="block.alt || ''"
        loading="lazy"
        @error="handleImageError"
      >
      <video v-else-if="block.type === 'video'" :src="block.src" controls preload="metadata"></video>
      <div v-else-if="block.type === 'embed' && block.placeholder" class="news-article-media-placeholder">
        <i class="fa-brands fa-youtube"></i>
        <span>{{ block.title || 'Video unavailable' }}</span>
      </div>
      <iframe
        v-else-if="block.type === 'embed'"
        :class="{ 'is-twitch-embed': isTwitchEmbed(block) }"
        :src="embedSource(block)"
        :title="block.title"
        loading="lazy"
        allow="accelerometer; autoplay; clipboard-write; encrypted-media; fullscreen; gyroscope; picture-in-picture; web-share"
        referrerpolicy="strict-origin-when-cross-origin"
        allowfullscreen
      ></iframe>
      <ol v-else-if="block.type === 'list' && block.ordered">
        <li v-for="item in block.items" :key="item" v-html="linkedHtml(item)"></li>
      </ol>
      <ul v-else-if="block.type === 'list'">
        <li v-for="item in block.items" :key="item" v-html="linkedHtml(item)"></li>
      </ul>
    </template>
  </section>
</template>

<script>
import { ensureWowheadTooltips, refreshWowheadTooltips } from '../utils/wowheadTooltips'
import { refreshRedditEmbeds } from '../utils/redditEmbeds'
import { articleTextFromHtml, sanitizeArticleHtml } from '../utils/sanitizeHtml'

export default {
  name: 'NewsArticleContent',
  props: {
    blocks: {
      type: Array,
      default: () => [],
    },
  },
  emits: ['open-source-article'],
  // Deliberately not in `data()`: Vue would make the Map reactive, and `linkedHtml` both reads
  // and writes it from inside the render pass, which retriggers the render effect and makes
  // `updated()` (and every tooltip refresh it schedules) run twice per article.
  created() {
    this.htmlCache = new Map()
  },
  computed: {
    displayBlocks() {
      const sourceBlocks = this.blocks
        .filter((block) => !this.isTechnicalAuthorBlock(block))
        .map((block) => this.twitchEmbedBlock(this.redditEmbedBlock(block)))
      const blocks = []

      for (let index = 0; index < sourceBlocks.length; index += 1) {
        const block = sourceBlocks[index]
        const previous = blocks[blocks.length - 1]
        const next = sourceBlocks[index + 1]

        if (this.isBrokenInlineVideo(block, previous, next)) {
          if (this.canMergeTextBlocks(previous, next)) {
            blocks[blocks.length - 1] = this.mergeTextBlocks(previous, next)
            index += 1
          }
          continue
        }

        blocks.push(block)
      }

      return blocks
    },
  },
  watch: {
    // The cache is keyed by raw html; a new article shares none of those keys, so keeping the
    // old entries would only grow the Map for the component's lifetime.
    blocks() {
      this.htmlCache.clear()
    },
  },
  mounted() {
    this.prepareLinks()
    this.prepareRedditEmbeds()
  },
  updated() {
    this.prepareLinks()
    this.prepareRedditEmbeds()
  },
  methods: {
    // WoW Hub tints class headings and specialization cards with the in-game class colour.
    headingStyle(block = {}) {
      return block.accent ? { '--news-article-accent': block.accent } : {}
    },
    redditEmbedBlock(block = {}) {
      if (block.type === 'reddit' && this.isRedditPostUrl(block.url)) return block
      if (block.type !== 'quote') return block

      const template = document.createElement('template')
      template.innerHTML = String(block.html || block.text || '')
      const text = (template.content.textContent || '').replace(/\s+/g, ' ').trim()
      if (!/^(?:reddit|reddit post|https?:\/\/\S+)$/i.test(text)) return block

      const link = Array.from(template.content.querySelectorAll('a[href]'))
        .find((candidate) => this.isRedditPostUrl(candidate.href))

      return link ? { type: 'reddit', url: link.href } : block
    },
    isTwitchEmbed(block = {}) {
      return block.provider === 'twitch' || Boolean(this.twitchEmbedUrl(block.src))
    },
    twitchEmbedBlock(block = {}) {
      if (block.type === 'embed' && this.twitchEmbedUrl(block.src)) {
        return { ...block, provider: 'twitch', title: block.title || 'Twitch video' }
      }
      if (!['html', 'paragraph', 'quote'].includes(block.type) || typeof document === 'undefined') return block

      const template = document.createElement('template')
      template.innerHTML = String(block.html || block.text || '')
      const text = (template.content.textContent || '').replace(/\s+/g, ' ').trim()
      if (!/^(?:twitch|twitch clip|https?:\/\/\S+)$/i.test(text)) return block

      const link = Array.from(template.content.querySelectorAll('a[href]'))
        .find((candidate) => this.twitchEmbedUrl(candidate.href))
      const source = link?.href || (/^https?:\/\//i.test(text) ? text : '')

      return this.twitchEmbedUrl(source)
        ? { type: 'embed', src: source, title: 'Twitch video', provider: 'twitch' }
        : block
    },
    embedSource(block = {}) {
      return this.twitchEmbedUrl(block.src) || block.src
    },
    // Kept in sync with `twitchEmbedUrl` in src/services/cloudFlareWorker.js. The worker is a
    // separate deployment and is not part of this bundle, so the two copies stay side by side;
    // this one additionally appends the `parent` parameter Twitch requires from a browser.
    twitchEmbedUrl(value = '') {
      if (!value || typeof window === 'undefined') return ''

      try {
        const source = new URL(value)
        const host = source.hostname.replace(/^www\./i, '').toLowerCase()
        const path = source.pathname.split('/').filter(Boolean)
        let embed = null

        if (host === 'clips.twitch.tv') {
          const clip = source.searchParams.get('clip')
            || (path[0]?.toLowerCase() === 'embed' ? '' : path[0])
          if (clip) {
            embed = new URL('https://clips.twitch.tv/embed')
            embed.searchParams.set('clip', clip)
          }
        } else if (host === 'player.twitch.tv') {
          embed = new URL('https://player.twitch.tv/')
          ;['video', 'channel', 'collection', 'time'].forEach((parameter) => {
            const parameterValue = source.searchParams.get(parameter)
            if (parameterValue) embed.searchParams.set(parameter, parameterValue)
          })
          if (![...embed.searchParams.keys()].length) return ''
        } else if (host === 'twitch.tv') {
          const clipIndex = path.findIndex((segment) => segment.toLowerCase() === 'clip')
          if (clipIndex !== -1 && path[clipIndex + 1]) {
            embed = new URL('https://clips.twitch.tv/embed')
            embed.searchParams.set('clip', path[clipIndex + 1])
          } else if (path[0]?.toLowerCase() === 'videos' && /^\d+$/.test(path[1] || '')) {
            embed = new URL('https://player.twitch.tv/')
            embed.searchParams.set('video', path[1])
          }
        }

        if (!embed) return ''

        embed.searchParams.set('parent', window.location.hostname || 'rio-dashboard.andrii-ilutsa.me')
        embed.searchParams.set('autoplay', 'false')
        return embed.toString()
      } catch {
        return ''
      }
    },
    isRedditPostUrl(value) {
      try {
        const url = new URL(value)
        return /(^|\.)reddit\.com$/i.test(url.hostname)
          && /^\/(?:r\/[^/]+\/)?comments\/[a-z0-9]+(?:\/|$)/i.test(url.pathname)
      } catch {
        return false
      }
    },
    prepareRedditEmbeds() {
      this.$nextTick(() => {
        if (this.$el.querySelector('blockquote.reddit-embed-bq')) refreshRedditEmbeds()
      })
    },
    isTechnicalAuthorBlock(block = {}) {
      const value = block.type === 'list'
        ? (block.items || []).join(' ')
        : block.html || block.text || ''
      const decoded = this.decodeRepeatedEntities(value)
      return /(?:<|&lt;)h[1-6]\b/i.test(decoded)
        && /(?:action=profile|profile;u=)/i.test(decoded)
    },
    decodeRepeatedEntities(value = '') {
      if (typeof document === 'undefined') return String(value || '')
      const element = document.createElement('textarea')
      let decoded = String(value || '')

      for (let attempt = 0; attempt < 3; attempt += 1) {
        element.innerHTML = decoded
        const next = element.value
        if (next === decoded) break
        decoded = next
      }

      return decoded
    },
    isBrokenInlineVideo(block = {}, previous = {}, next = {}) {
      if (block.type !== 'embed' || block.provider !== 'youtube') return false
      if (!this.isArticleTextBlock(previous) || !this.isArticleTextBlock(next)) return false

      const previousText = this.blockPlainText(previous)
      const nextText = this.blockPlainText(next)
      if (!previousText || !nextText) return false

      return /^[,.;!?)]/.test(nextText)
        || (!/[.!?;:]$/.test(previousText) && /^[a-zа-яёіїєґ]/.test(nextText))
    },
    isArticleTextBlock(block = {}) {
      return block.type === 'paragraph' || (block.type === 'html' && block.tag === 'p')
    },
    canMergeTextBlocks(left = {}, right = {}) {
      return (left.type === 'paragraph' && right.type === 'paragraph')
        || (left.type === 'html' && left.tag === 'p' && right.type === 'html' && right.tag === 'p')
    },
    mergeTextBlocks(left, right) {
      const rightText = this.blockPlainText(right)
      const separator = /^[,.;!?)]/.test(rightText) ? '' : ' '

      if (left.type === 'paragraph') {
        return {
          ...left,
          text: `${String(left.text || '').trimEnd()}${separator}${String(right.text || '').trimStart()}`,
        }
      }

      return {
        ...left,
        html: `${String(left.html || '').trimEnd()}${separator}${String(right.html || '').trimStart()}`,
      }
    },
    blockPlainText(block = {}) {
      const value = block.type === 'list'
        ? (block.items || []).join(' ')
        : block.html || block.text || ''
      return articleTextFromHtml(value).replace(/\s+/g, ' ').trim()
    },
    linkedText(value = '') {
      if (typeof document === 'undefined') return String(value || '')
      const element = document.createElement('span')
      element.textContent = String(value || '')
      return this.linkedHtml(element.innerHTML)
    },
    linkedHtml(value = '') {
      const source = String(value || '')
      if (this.htmlCache.has(source)) return this.htmlCache.get(source)
      if (typeof document === 'undefined') return source

      const template = document.createElement('template')
      // Article HTML comes from third-party pages and is re-decoded upstream, so it is
      // sanitized here — the single point every v-html in this component flows through.
      template.innerHTML = sanitizeArticleHtml(source)
      const textNodes = []
      const walker = document.createTreeWalker(template.content, NodeFilter.SHOW_TEXT)
      let node = walker.nextNode()

      while (node) {
        if (!node.parentElement?.closest('a, code, pre, script, style') && /https?:\/\//i.test(node.textContent || '')) {
          textNodes.push(node)
        }
        node = walker.nextNode()
      }

      textNodes.forEach((textNode) => this.linkifyTextNode(textNode))
      const html = template.innerHTML
      this.htmlCache.set(source, html)
      return html
    },
    linkifyTextNode(textNode) {
      const text = textNode.textContent || ''
      const pattern = /https?:\/\/[^\s<>"']+/gi
      const fragment = document.createDocumentFragment()
      let cursor = 0
      let match

      while ((match = pattern.exec(text))) {
        let url = match[0]
        let trailing = ''

        while (/[),.;:!?]$/.test(url)) {
          trailing = url.slice(-1) + trailing
          url = url.slice(0, -1)
        }

        fragment.append(document.createTextNode(text.slice(cursor, match.index)))
        const link = document.createElement('a')
        link.href = url
        link.textContent = url
        fragment.append(link)
        if (trailing) fragment.append(document.createTextNode(trailing))
        cursor = match.index + match[0].length
      }

      fragment.append(document.createTextNode(text.slice(cursor)))
      textNode.replaceWith(fragment)
    },
    prepareLinks() {
      this.$nextTick(() => {
        // Sanitization drops `loading` (ALLOWED_URI_REGEXP is applied to its value too),
        // so lazy loading is restored on the rendered nodes.
        this.$el.querySelectorAll('img:not([loading])').forEach((image) => {
          image.loading = 'lazy'
        })

        this.$el.querySelectorAll('a[href]').forEach((link) => {
          const href = link.href

          if (this.isSourceArticleUrl(href)) {
            link.removeAttribute('target')
            link.removeAttribute('rel')
            link.classList.add('news-internal-article-link')
          } else {
            link.target = '_blank'
            link.rel = 'noopener noreferrer'
          }

          const tooltipData = this.wowheadTooltipData(href)
          if (tooltipData) link.dataset.wowhead = tooltipData
        })

        // Both source sites render entity links with their Wowhead icon; the reader matches that.
        ensureWowheadTooltips({ renameLinks: true, iconizeLinks: true, iconSize: 'small' })
        refreshWowheadTooltips()
      })
    },
    handleLinkClick(event) {
      const link = event.target?.closest?.('a[href]')
      if (!link || !this.$el.contains(link) || !this.isSourceArticleUrl(link.href)) return

      event.preventDefault()
      this.$emit('open-source-article', link.href)
    },
    // WoW Hub articles live at /YYYY/MM/slug/, so links between them open in the reader
    // instead of leaving the app.
    isSourceArticleUrl(value) {
      try {
        const url = new URL(value)
        return /^(?:www\.)?wowhub\.com\.ua$/i.test(url.hostname) && /^\/\d{4}\/\d{2}\/[^/]+\/?$/.test(url.pathname)
      } catch {
        return false
      }
    },
    wowheadTooltipData(value) {
      try {
        const url = new URL(value)
        if (!/(^|\.)wowhead\.com$/i.test(url.hostname)) return ''
        const match = url.pathname.match(/\/(item|spell|quest|npc|currency|event|achievement|affix|object)=(\d+)/i)
        return match ? `${match[1].toLowerCase()}=${match[2]}` : ''
      } catch {
        return ''
      }
    },
    handleImageError(event) {
      const image = event.currentTarget
      const src = String(image?.src || '')
      if (!image) return

      if (image.dataset.fallbackTried !== 'true' && /\/uploads\/screenshots\/[^/]+\/\d+\.jpg(?:$|\?)/i.test(src)) {
        image.dataset.fallbackTried = 'true'
        image.src = src.replace(/\.jpg(?=$|\?)/i, '.png')
        return
      }

      image.classList.add('is-broken')
      image.removeAttribute('src')
    },
  },
}
</script>
